// Access control catalog.
// Feature keys live in code, their values per role live in the PocketBase `roles` collection
// and per-user overrides live in the `user_access` collection (see docs/ACCESS_CONTROL_SETUP.md).
// When those collections are missing or unavailable, DEFAULT_ROLE_PERMISSIONS is used,
// which mirrors the behaviour the app had before access control was introduced.

export const MASTER_ROLE = 'master';
export const ADMIN_ROLE = 'admin';
export const DEFAULT_ROLE = 'user';

// System roles can not be deleted, PocketBase rules and app logic depend on them.
// Master is the owner role and is never assignable from the UI.
export const SYSTEM_ROLES = ['user', 'admin', 'master'];
// Roles created by "Initialize roles". Until the `roles` collection has records,
// these roles work with DEFAULT_ROLE_PERMISSIONS. After that vip is a regular custom role.
export const INITIAL_ROLES = ['user', 'vip', 'admin', 'master'];
export const ROLE_KEY_PATTERN = /^[a-z][a-z0-9_-]{1,31}$/;

export const DEFAULT_ROLE_NAMES = {
    user: 'User',
    vip: 'Vip',
    admin: 'Admin',
    master: 'Master'
};

export const PERMISSION_GROUPS = [
    { key: 'projects', title: 'Projects' },
    { key: 'data', title: 'Data' },
    { key: 'docs', title: 'Documentation' }
];

// Features configurable per role and per user
export const PERMISSIONS = [
    {
        key: 'projects.public.view',
        group: 'projects',
        icon: 'fa-globe',
        title: 'Public projects',
        description: 'See and open public projects of other users.'
    },
    {
        key: 'projects.code',
        group: 'projects',
        icon: 'fa-code',
        title: 'Code of other projects',
        description: 'Code tab in public projects of other users. Own and shared projects always show code.'
    },
    {
        key: 'projects.interface',
        group: 'projects',
        icon: 'fa-window-restore',
        title: 'Interface constructor',
        description: 'Files & Interface tab: run parameters, XLSX files and data selection.'
    },
    {
        key: 'projects.publish',
        group: 'projects',
        icon: 'fa-share-square',
        title: 'Publish projects',
        description: 'Switch own projects between private and public.'
    },
    {
        key: 'projects.ai',
        group: 'projects',
        icon: 'fa-magic',
        title: 'AI assistant',
        description: 'AI assistant button in the code editor.'
    },
    {
        key: 'datastorage',
        group: 'data',
        icon: 'fa-database',
        title: 'Data storage',
        description: 'Data storage pages and selecting stored data in projects.'
    },
    {
        key: 'docs.agentSkills',
        group: 'docs',
        icon: 'fa-file-alt',
        title: 'Agent skill files',
        description: 'Visualization skill files in the documentation.'
    }
];

export const PERMISSION_KEYS = PERMISSIONS.map(permission => permission.key);

// Administration is not configurable: it belongs to the admin and master roles only,
// the same roles PocketBase rules trust (see docs/ACCESS_CONTROL_SETUP.md).
export const ADMIN_ROLES = [ADMIN_ROLE, MASTER_ROLE];
export const ADMIN_PERMISSION_KEYS = [
    'admin.users.view',
    'admin.users.manage',
    'admin.logs',
    'admin.roles'
];

export const DEFAULT_ROLE_DESCRIPTIONS = {
    user: 'Base role. New users get it automatically, users of deleted roles are moved here.',
    vip: 'Extended project tools and data storage.',
    admin: 'Administration of users, roles and logs.',
    master: 'Owner. Always has full access and can not be changed.'
};

// Used while the `roles` collection is empty or unavailable. Mirrors the pre access control behaviour.
export const DEFAULT_ROLE_PERMISSIONS = {
    user: [
        'projects.public.view',
        'projects.code'
    ],
    vip: [
        'projects.public.view',
        'projects.code',
        'projects.interface',
        'projects.publish',
        'projects.ai',
        'datastorage',
        'docs.agentSkills'
    ],
    admin: [...PERMISSION_KEYS]
};

export const ADMIN_PAGES = [
    { path: '/admin/users', title: 'Users', icon: 'fa-users', permission: 'admin.users.view' },
    { path: '/admin/roles', title: 'Roles', icon: 'fa-key', permission: 'admin.roles' },
    { path: '/admin/logs', title: 'Logs', icon: 'fa-chart-bar', permission: 'admin.logs' }
];

export function getPermission(key) {
    return PERMISSIONS.find(permission => permission.key == key);
}

export function isAdminPermission(key) {
    return ADMIN_PERMISSION_KEYS.indexOf(key) != (-1);
}

export function normalizeRole(role) {
    const value = String(role || '').trim().toLowerCase();
    return value || DEFAULT_ROLE;
}

export function isSystemRole(role) {
    return SYSTEM_ROLES.indexOf(normalizeRole(role)) != (-1);
}

export function hasAdminAccess(role) {
    return ADMIN_ROLES.indexOf(normalizeRole(role)) != (-1);
}

export function sanitizePermissionList(list) {
    if (!Array.isArray(list)) return null;
    return PERMISSION_KEYS.filter(key => list.indexOf(key) != (-1));
}

export function sanitizeOverrides(overrides) {
    const result = {};
    if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) return result;

    PERMISSION_KEYS.forEach(key => {
        if (typeof overrides[key] === 'boolean') result[key] = overrides[key];
    });

    return result;
}

// rolePermissions: permission keys of the user's role (from DB or defaults).
// Returns { key: { allowed, source } } where source is 'owner', 'role', 'allow' or 'deny'.
export function resolvePermissionDetails(role, rolePermissions, overrides) {
    const roleKey = normalizeRole(role);
    const cleanOverrides = sanitizeOverrides(overrides);
    const result = {};

    PERMISSION_KEYS.forEach(key => {
        if (roleKey == MASTER_ROLE) {
            result[key] = { allowed: true, source: 'owner' };
            return;
        }

        const fromRole = rolePermissions.indexOf(key) != (-1);

        if (cleanOverrides[key] === false) {
            result[key] = { allowed: false, fromRole, source: 'deny' };
        } else if (cleanOverrides[key] === true) {
            result[key] = { allowed: true, fromRole, source: 'allow' };
        } else {
            result[key] = { allowed: fromRole, fromRole, source: 'role' };
        }
    });

    return result;
}
